import {
  error,
  outline,
  surfaceDim,
  onSurfaceVariant,
  surfaceBright,
  onSurface,
  primary,
  onSurfacePlaceholder,
} from 'core/styles'

const baseContainerStyles = (provided, _state) => ({
  ...provided,
  fontSize: '14px',
})

const controlStyles = {
  alignItems: 'normal',
  boxSizing: 'border-box',
  cursor: 'pointer',
  borderRadius: '8px',
  border: `1px solid ${outline}`,
  margin: '0',
  padding: '0',
  height: '32px',
  minHeight: 'auto',
  fontSize: '14px',
  fontWeight: 500,
}

export const baseControlStyles = (provided, state) => {
  const { isFocused, menuIsOpen } = state || {}

  return {
    ...provided,
    ...controlStyles,
    ...(isFocused || menuIsOpen ?
      {
        /**
         * Using !important to override the hover styles
         * for inline drop downs, since those styles are
         * slightly more specific than this.
         */
        borderColor: `${primary} !important`,
        boxShadow: 'none',
      }
    : {}),
  }
}

const baseDropdownIndicator = (provided, _state) => ({
  ...provided,
  padding: '4px 8px',
})

const baseIndicatorSeparatorStyles = (provided) => ({
  ...provided,
  display: 'none',
  paddingLeft: '0',
})

export const errorControlStylesCompact = (provided) => ({
  ...provided,
  ...controlStyles,
  borderColor: `${error} !important`,
  boxShadow: 'none',
})

const baseMenuStyles = (provided, _state) => ({
  ...provided,
  display: 'inline-flex',
  overflow: 'hidden',
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  borderRadius: '12px',
  border: `1px solid ${outline}`,
  backgroundColor: outline,
  width: 'auto',
  fontSize: '14px',
  fontWeight: 500,
})

const baseMenuListStyles = (provided, _state) => ({
  ...provided,
  padding: 0,
  display: 'grid',
  gap: '1px',
})

const baseMultiValueLabelStyles = (provided, state) => {
  const { selectProps } = state || {}
  const { wrap } = selectProps || {}

  return {
    ...provided,
    whiteSpace: wrap ? 'wrap' : 'nowrap',
  }
}

const baseOptionStyles = (provided, state) => ({
  ...provided,
  display: 'flex',
  svg: {
    fill: onSurface,
  },
  cursor: state.isDisabled ? 'not-allowed' : 'pointer',
  padding: '6px',
  backgroundColor:
    state.isSelected ? surfaceBright
    : state.isFocused ? surfaceDim
    : surfaceBright,
  color: state.isSelected ? onSurface : onSurfaceVariant,
  ':hover': {
    backgroundColor: state.isDisabled ? 'transparent' : surfaceDim,
  },
})

const basePlaceholderStyles = (provided, _state) => ({
  ...provided,
  // react-select default overrides
  position: 'relative',
  top: '0',
  transform: 'none',
  margin: '0',
  // end react-select overrides ---
  color: onSurfacePlaceholder,
  whiteSpace: 'nowrap',
})

const baseSingleValueStyles = (provided, _state) => ({
  ...provided,
  display: 'flex',
  alignItems: 'center',
  marginLeft: '0',
  marginRight: '0',
  maxWidth: 'none',
  height: '100%',
  lineHeight: '1',
})

const baseValueContainerStyles = (provided, state) => {
  const { selectProps } = state || {}
  const { wrap } = selectProps || {}

  return {
    ...provided,
    flexWrap: wrap ? 'wrap' : 'nowrap',
    padding: '2px 0 2px 8px',
    height: '100%',
    lineHeight: '1',
  }
}

const baseIndicatorsContainerStyles = (provided) => ({
  ...provided,
  '> div': {
    padding: '0 8px',
  },
  '> div > svg': {
    width: '16px',
    height: '16px',
  },
})

const customStyles = {
  container: (provided, state) => baseContainerStyles(provided, state),
  control: (provided, state) => baseControlStyles(provided, state),
  dropdownIndicator: (provided, state) => baseDropdownIndicator(provided, state),
  indicatorSeparator: (provided) => baseIndicatorSeparatorStyles(provided),
  menu: (provided, state) => baseMenuStyles(provided, state),
  menuList: (provided, state) => baseMenuListStyles(provided, state),
  multiValueLabel: (provided, state) => baseMultiValueLabelStyles(provided, state),
  option: (provided, state) => baseOptionStyles(provided, state),
  placeholder: (provided, state) => basePlaceholderStyles(provided, state),
  singleValue: (provided, state) => baseSingleValueStyles(provided, state),
  valueContainer: (provided, state) => baseValueContainerStyles(provided, state),
  indicatorsContainer: (provided) => baseIndicatorsContainerStyles(provided),
}

export default customStyles
